import { useState } from "react";
import { ArrowRight, Check, MessageCircle } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const WHATSAPP = "https://wa.me/34632628051";
const inputCls = "bg-white border border-stone-200 rounded-full px-5 py-3 text-sm text-[#2E2A26] placeholder:text-[#2E2A26]/30 focus:outline-none focus:border-[#C85A38]";

const OffsiteEnquiryForm = () => {
  const [company, setCompany] = useState("");
  const [contactName, setContactName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [teamSize, setTeamSize] = useState("");
  const [targetDates, setTargetDates] = useState("");
  const [message, setMessage] = useState("");
  const [website, setWebsite] = useState(""); // honeypot
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!company.trim() || !contactName.trim() || !email.trim()) return;
    const cleanedPhone = phone.trim().replace(/[\s\-().]/g, "");
    if (cleanedPhone && !/^\+\d{6,15}$/.test(cleanedPhone)) {
      toast.error("Please include your country code (e.g. +34 612 345 678)");
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/offsite/enquiry`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          company: company.trim(),
          contact_name: contactName.trim(),
          email: email.trim(),
          phone: phone.trim(),
          team_size: teamSize,
          target_dates: targetDates.trim(),
          message: message.trim(),
          website: website.trim(),
        }),
      });
      if (!res.ok) throw new Error("submit failed");
      if (typeof window !== "undefined" && window.gtag) {
        window.gtag("event", "offsite_enquiry", { event_category: "conversion" });
      }
      setDone(true);
    } catch {
      toast.error("That didn't send — please try again, or message Steve on WhatsApp.");
    }
    setSubmitting(false);
  };

  const whatsappHref = () => {
    const bits = [
      `Hi Steve! I'm enquiring about a company off-site at Savi for ${company.trim() || "our team"}`,
      teamSize ? `(${teamSize})` : "",
      targetDates.trim() ? `around ${targetDates.trim()}` : "",
    ].filter(Boolean).join(" ");
    return `${WHATSAPP}?text=${encodeURIComponent(`${bits}. Just sent an enquiry through the site — could we chat?`)}`;
  };

  if (done) {
    return (
      <div className="bg-[#FBF7F1] rounded-3xl p-8 sm:p-12 border border-[#C85A38]/20 text-center" data-testid="offsite-success">
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[#C85A38]/10 mb-5">
          <Check className="w-7 h-7 text-[#C85A38]" />
        </div>
        <h3 className="font-serif text-2xl sm:text-3xl text-[#2E2A26] mb-3">
          Got it — Steve&apos;s on it.
        </h3>
        <p className="text-[#2E2A26]/60 text-sm sm:text-base max-w-md mx-auto leading-relaxed mb-6">
          He&apos;ll reply personally within 24 hours with availability for your window and a custom quote. Keep an eye on your inbox — and WhatsApp him if you&apos;d rather move faster.
        </p>
        <a
          href={whatsappHref()}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1fb958] text-white rounded-full px-7 py-3 text-sm font-medium transition-colors"
          data-testid="offsite-whatsapp-success-cta"
        >
          <MessageCircle className="w-4 h-4" /> Prefer to chat? Message Steve now
        </a>
      </div>
    );
  }

  return (
    <div className="bg-[#FBF7F1] rounded-3xl p-6 sm:p-10 border border-stone-200" data-testid="offsite-form">
      <div className="text-center mb-7">
        <p className="text-[#C85A38] text-xs font-medium tracking-[0.2em] uppercase mb-3">Request a custom quote</p>
        <h3 className="font-serif text-2xl sm:text-3xl text-[#2E2A26] mb-3">
          Tell us about your team.
        </h3>
        <p className="text-[#2E2A26]/60 text-sm sm:text-base max-w-xl mx-auto leading-relaxed">
          Steve reads every enquiry himself and comes back within 24 hours with availability and a quote shaped around your goals. No sales sequence, no pressure.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="max-w-xl mx-auto space-y-3">
        {/* Honeypot */}
        <input
          type="text"
          value={website}
          onChange={(e) => setWebsite(e.target.value)}
          className="hidden"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <input type="text" autoComplete="organization" required value={company} onChange={(e) => setCompany(e.target.value)} placeholder="Company / team name" className={inputCls} data-testid="offsite-company" />
          <input type="text" autoComplete="name" required value={contactName} onChange={(e) => setContactName(e.target.value)} placeholder="Your name" className={inputCls} data-testid="offsite-contact-name" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <input type="email" autoComplete="email" inputMode="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Work email" className={inputCls} data-testid="offsite-email" />
          <input type="tel" autoComplete="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="WhatsApp / phone (with country code) — optional" className={inputCls} data-testid="offsite-phone" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <select value={teamSize} onChange={(e) => setTeamSize(e.target.value)} className={inputCls} data-testid="offsite-team-size">
            <option value="">Team size</option>
            <option value="Up to 6 people">Up to 6 people</option>
            <option value="7–10 people">7–10 people</option>
            <option value="11–14 people">11–14 people</option>
            <option value="14+ (let's talk)">14+ (let&apos;s talk)</option>
          </select>
          <input type="text" value={targetDates} onChange={(e) => setTargetDates(e.target.value)} placeholder="Rough dates (e.g. a week in November)" className={inputCls} data-testid="offsite-dates" />
        </div>
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="What are you hoping to get out of it? (optional)"
          rows={3}
          className="w-full bg-white border border-stone-200 rounded-2xl px-5 py-3 text-sm text-[#2E2A26] placeholder:text-[#2E2A26]/30 focus:outline-none focus:border-[#C85A38] resize-none"
          data-testid="offsite-message"
        />

        <button
          type="submit"
          disabled={submitting || !company || !contactName || !email}
          className="w-full bg-[#C85A38] hover:bg-[#A84830] disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium tracking-wide rounded-full px-6 py-3.5 transition-colors flex items-center justify-center gap-2 mt-2"
          data-testid="offsite-submit"
        >
          {submitting ? "Sending..." : "Request a quote"}
          {!submitting && <ArrowRight className="w-4 h-4" />}
        </button>

        <div className="grid grid-cols-3 gap-2 pt-4 text-center" data-testid="offsite-next-steps">
          {[
            ["1", "Steve replies within 24h with availability"],
            ["2", "You get a custom quote + draft programme"],
            ["3", "Confirm, and we handle the rest"],
          ].map(([n, t]) => (
            <div key={n} className="px-1">
              <span className="inline-flex w-6 h-6 rounded-full bg-[#C85A38]/10 text-[#C85A38] text-xs font-semibold items-center justify-center mb-1.5">{n}</span>
              <p className="text-[#2E2A26]/55 text-[11px] leading-snug">{t}</p>
            </div>
          ))}
        </div>
      </form>
    </div>
  );
};

export default OffsiteEnquiryForm;
